/**
 * Turns the model's citations into quiet footnotes. The cited name stays in
 * the sentence as plain text — the prompts have Rob use it as a noun ("In
 * Grace he talks about…") — and a small superscript number after it links to
 * the exact moment or post. renderCitationFooter lists each distinct source
 * once, numbered to match. A reference that trails a finished sentence
 * ("…in our troubles. **[Title | 3:00]** Churches…") or sits alone in
 * parentheses keeps only its number.
 *
 * Recognized shapes (the prompts ask for the first three):
 *   **[Exact Sermon Title | 4:12]**    **[Title | 4:12-5:30]**
 *   **[Blog: Post Title]**              **[Genius · Chapter Nine]**
 *   **Title | 4:12**                    [Title | 4:12]
 *   [timestamp:4:12]  [4:12]  timestamp:4:12   (single-sermon mode)
 *   **Genius · Chapter 9**              only with `ground` (below)
 *
 * With `ground` (what the server says was in front of the model; Phase 2 of
 * docs/plans/genius-grounding.md), a chapter of Genius is a number, and in the
 * app a reader button, only if it was read for this answer; a chapter it
 * never read is its name in plain words. Without it, exactly as before.
 *
 * Title resolution order: the sermons the search actually retrieved for this
 * answer (`sources`), then the full catalog (`videos`), exact → normalized →
 * containment. A title that resolves nowhere stays plain text with no number:
 * it is more likely invented than merely missing, and a numbered source that
 * leads nowhere would be worse than none. Input is HTML-escaped text (see
 * renderMarkdown), so captured titles are decoded before matching and
 * re-escaped in the output.
 */

const ENTITIES = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'" };

function decodeEntities(text) {
  return String(text || '').replace(/&(amp|lt|gt|quot|#39);/g, (m) => ENTITIES[m] || m);
}

function escapeHtml(text) {
  return String(text || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

export function parseTimestampToSeconds(ts) {
  const parts = String(ts).split(':').map((n) => parseInt(n, 10));
  if (parts.length === 2) {
    const [m, s] = parts;
    return (m || 0) * 60 + (s || 0);
  }
  if (parts.length === 3) {
    const [h, m, s] = parts;
    return (h || 0) * 3600 + (m || 0) * 60 + (s || 0);
  }
  return 0;
}

/**
 * Video links open this many seconds before the cited moment, so the line
 * arrives with a breath of context instead of mid-word. Labels keep the true
 * timecode; only the link starts early.
 */
export const LEAD_IN_SECONDS = 5;

export function buildTimestampUrl(baseUrl, seconds) {
  const t = Math.max(0, (Math.floor(Number(seconds)) || 0) - LEAD_IN_SECONDS);
  try {
    const url = new URL(baseUrl);
    url.searchParams.set('t', String(t));
    return url.toString();
  } catch (e) {
    const delim = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${delim}t=${t}`;
  }
}

const YOUTUBE_HOST = /(^|\.)(youtube\.com|youtu\.be)$/i;

/**
 * The same lead-in for links built elsewhere: the server deep-links each
 * retrieved sermon to its chunk's first timecode, the true moment
 * (functions/chat.js, sourceFromResult). Only YouTube links with a
 * whole-second `t` change; a blog post or anything else passes through.
 */
export function withLeadIn(url) {
  if (!url) return url;
  try {
    const u = new URL(url);
    const t = u.searchParams.get('t');
    if (!YOUTUBE_HOST.test(u.hostname) || !t || !/^\d+s?$/.test(t)) return url;
    u.searchParams.delete('t');
    return buildTimestampUrl(u.toString(), parseInt(t, 10));
  } catch (e) {
    return url;
  }
}

function normalizeTitle(title) {
  return (title || '')
    .toLowerCase()
    .replace(/^[^-]+-\s*/, '')
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** 'blog' | 'book' | 'video', decided the way the server does (saved chats keep only videoId). */
function sourceKind(source) {
  const id = String(source?.videoId || '');
  if (source?.contentType === 'blog' || /^blog:/i.test(id)) return 'blog';
  if (source?.contentType === 'book' || /^book-/i.test(id)) return 'book';
  return 'video';
}

/**
 * The prompts' own stand-ins, copied literally: "Series - Sermon Title",
 * "Post Title", "Exact Title". They name nothing, so they never resolve, not
 * even by containment against a real title that shares a word with them
 * (a post called "Title", the one sermon of a Focused Study).
 */
const PLACEHOLDER_TITLE = /^(?:(?:exact|record|full|series|sermon|post|blog|video|clip)\s*[-–—:·|]?\s*)*title$/i;
const isPlaceholderTitle = (title) => PLACEHOLDER_TITLE.test(decodeEntities(title).trim());

function findByTitle(list, videoRef) {
  if (!list?.length) return null;
  const lowerRef = videoRef.toLowerCase().trim();
  const normalizedRef = normalizeTitle(videoRef);

  const exact = list.find((v) => v.title?.toLowerCase().trim() === lowerRef);
  if (exact) return exact;

  const normalizedExact = list.find((v) => normalizeTitle(v.title) === normalizedRef);
  if (normalizedExact) return normalizedExact;

  const includes = list.find((v) => {
    const lowerTitle = v.title?.toLowerCase() || '';
    return lowerTitle.length > 3 && (lowerTitle.includes(lowerRef) || lowerRef.includes(lowerTitle));
  });
  if (includes) return includes;

  const normalizedIncludes = list.find((v) => {
    const normalizedTitle = normalizeTitle(v.title);
    return normalizedTitle.length > 3 && (normalizedTitle.includes(normalizedRef) || normalizedRef.includes(normalizedTitle));
  });
  if (normalizedIncludes) return normalizedIncludes;

  return list.find((v) => (v.id || v.videoId)?.toLowerCase() === lowerRef) || null;
}

/** Sources first (this answer's own retrieval), then the catalog. */
export function resolveVideoByTitle(videos, videoRef, sources = []) {
  if (!videoRef) return videos?.[0] || null;
  const decoded = decodeEntities(videoRef);
  if (isPlaceholderTitle(decoded)) return null;
  const fromSources = findByTitle(
    (sources || [])
      .filter((s) => s.videoUrl && sourceKind(s) === 'video')
      .map((s) => ({ ...s, videoUrl: s.videoUrl.replace(/[&?]t=\d+$/, '') })),
    decoded
  );
  if (fromSources) return fromSources;
  return findByTitle(videos, decoded);
}

function baseVideoUrl(video) {
  const url = video?.videoUrl || (video?.videoId || video?.id ? `https://www.youtube.com/watch?v=${video.videoId || video.id}` : null);
  if (!url) return null;
  try {
    const u = new URL(url);
    u.searchParams.delete('t');
    return u.toString();
  } catch (e) {
    return url;
  }
}

const isWebUrl = (url) => /^https?:\/\//i.test(String(url || ''));

/**
 * A link the model put inside a citation — "**[[The Net](https://…) - Grace |
 * 31:20]**", the prompt's "link the church" carried into a title. The title
 * is a title, and the citation is already the link, so the inner one goes.
 * Only citation-shaped brackets: a timecode after it, or Blog:/Genius before.
 */
const LINK_IN_CITATION = /\[([^[\]\n]*)\[([^[\]\n]+)\]\(https?:\/\/[^\s)]*\)([^[\]\n]*)\]/g;
const isCitationShape = (before, after) =>
  /\|\s*(?:\d{1,2}:)?\d{1,2}:\d{2}/.test(after) || /^\s*(?:[Bb]log:|Genius\b)/.test(before);

// While the patterns below run, each recognized citation is held as "<n>":
// escaped text never contains a raw "<", so a hold can't collide with the
// model's words, and the title classes exclude "<" so no pattern reaches
// across one.

/**
 * Blog citations: **[Blog: The Problem With Pain]**. Numbered and linked only
 * when the post is among the sources this answer actually retrieved — an
 * unresolved title is more likely invented than merely missing, and a broken
 * link to thenet.org would be worse than plain text. A URL the model tacked
 * on is dropped either way.
 */
const BLOG_PATTERN = /(\*\*\s*)?\[\s*[Bb]log:\s*([^\]\n<]+?)\s*\](?:\(https?:\/\/[^\s)]*\))?(\s*\*\*)?/g;

/**
 * Book citations: **[Genius · Chapter Nine]**. Never a link — see prompts.js.
 * The separator is what tells the book from the sermon series, which is
 * "Genius - The Genius Of Sex" and always carries a timecode.
 */
const BOOK_PATTERN = /(\*\*\s*)?\[\s*(Genius(?:\s*[·•,:]\s*|\s*[-–—]?\s*(?=Chapter\b|Introduction\b))[^\]|\n<]*?)\s*\](?:\(https?:\/\/[^\s)]*\))?(\s*\*\*)?/g;

/**
 * The same, bold with no brackets: **Genius · Chapter 10** (Oct 16), which
 * the app used to leave as bold words. Read only when the answer came with
 * `ground` (below). The sermon series, "Genius - The Genius Of Sex", has a
 * dash before a title, never before a chapter, so it never matches.
 */
const BOOK_LOOSE = /\*\*\s*(Genius\s*(?:[·•,:]|[-–—](?=\s*(?:Chapter|Introduction)\b))\s*(?:Chapter\s+(?:[A-Za-z]+|\d{1,2})|Introduction)\b[^*\n|<]{0,60}?)\s*\*\*/g;

/**
 * What the server says was in front of the model for this answer
 * (functions/grounding.js clientGround, docs/plans/genius-grounding.md
 * Phase 2): { book: [chapter numbers], at, sermons }, or null. Only an object
 * with a book list counts; anything else is no ground at all, and the answer
 * renders exactly as it always did.
 */
function groundOf(value) {
  return value && typeof value === 'object' && Array.isArray(value.book) ? value : null;
}

const NUMBER_WORDS = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
  'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen', 'twenty'];

/**
 * "Chapter 11", "chapter eleven" → "chapter eleven"; "Introduction" →
 * "introduction"; null when there is no chapter, or none the book could have
 * ("Chapter Number" is the prompt's placeholder, not a chapter).
 */
function chapterKey(text) {
  const m = String(text || '').match(/\b(introduction|chapter\s+([a-z]+|\d+))\b/i);
  if (!m) return null;
  if (!m[2]) return 'introduction';
  const word = /^\d+$/.test(m[2]) ? NUMBER_WORDS[Number(m[2])] : m[2].toLowerCase();
  return word && NUMBER_WORDS.includes(word) ? `chapter ${word}` : null;
}

/** 11 for "chapter eleven", 0 for "introduction", null for anything else. */
function chapterNumberOf(key) {
  if (key === 'introduction') return 0;
  const n = key ? NUMBER_WORDS.indexOf(key.replace(/^chapter\s+/, '')) : -1;
  return n > 0 ? n : null;
}

/** The footer's name for a chapter: the retrieved one's full name, else the canonical short form. */
function bookTitle(citation, chapter) {
  if (chapter) return `Genius · ${String(chapter.title).split('—').pop().trim()}`;
  const key = chapterKey(citation);
  if (!key) return citation.replace(/\s*[·•]\s*/g, ' · ');
  return `Genius · ${key.replace(/\b\w/g, (c) => c.toUpperCase())}`;
}

function findBlogSource(sources, title) {
  if (isPlaceholderTitle(title)) return null;
  const wanted = normalizeTitle(decodeEntities(title));
  const blogs = (sources || []).filter((s) => sourceKind(s) === 'blog' && isWebUrl(s.videoUrl));
  return (
    blogs.find((s) => normalizeTitle(s.title) === wanted) ||
    blogs.find((s) => {
      const t = normalizeTitle(s.title);
      return t.length > 3 && (t.includes(wanted) || wanted.includes(t));
    }) ||
    null
  );
}

/** The retrieved chapter, for its name: "Genius: … — Chapter Eleven: The Genius Of Sex". */
function findBookSource(sources, citation) {
  const wanted = chapterKey(citation);
  if (!wanted) return null;
  return (sources || []).find((s) => sourceKind(s) === 'book' && chapterKey(String(s.title || '').split('—').pop()) === wanted) || null;
}

// En dashes in ranges and a URL the model tacked on are tolerated, so
// neither leaks into the sentence; the link is always the one resolved here.
const TITLE_TIMESTAMP_PATTERNS = [
  // **[Title | 4:12]**
  /\*\*\s*\[\s*([^|\]\n<]+?)\s*\|\s*((?:\d{1,2}:)?\d{1,2}:\d{2})(?:\s*[-–—]\s*((?:\d{1,2}:)?\d{1,2}:\d{2}))?\s*\](?:\(https?:\/\/[^\s)]*\))?\s*\*\*/g,
  // **Title | 4:12** (models often drop the brackets)
  /\*\*\s*([^|*[\]\n<]+?)\s*\|\s*((?:\d{1,2}:)?\d{1,2}:\d{2})(?:\s*[-–—]\s*((?:\d{1,2}:)?\d{1,2}:\d{2}))?\s*\*\*/g,
  // [Title | 4:12]
  /\[\s*([^|\]\n<]+?)\s*\|\s*((?:\d{1,2}:)?\d{1,2}:\d{2})(?:\s*[-–—]\s*((?:\d{1,2}:)?\d{1,2}:\d{2}))?\s*\](?:\(https?:\/\/[^\s)]*\))?/g,
];

// Single-sermon mode: [timestamp:9:36], [timestamp:24:55-25:25], [9:36], timestamp:9:36
const BARE_BRACKET = /\[(?:timestamp:)?((?:\d{1,2}:)?\d{1,2}:\d{2})(?:\s*[-–—]\s*((?:\d{1,2}:)?\d{1,2}:\d{2}))?\](?:\(https?:\/\/[^\s)]*\))?/g;
const BARE_WORD = /(^|\s)timestamp:((?:\d{1,2}:)?\d{1,2}:\d{2})(?:[-–—]((?:\d{1,2}:)?\d{1,2}:\d{2}))?(?=\s|$|[.,;)])/g;

/**
 * Numbers the distinct sources of one answer in reading order: the same
 * source at the same moment gets the same number every time it is cited.
 */
export function createCitationList() {
  const entries = [];
  const byKey = new Map();
  return {
    entries,
    number(cite) {
      if (!byKey.has(cite.key)) {
        entries.push({ ...cite, n: entries.length + 1 });
        byKey.set(cite.key, entries.length);
      }
      return byKey.get(cite.key);
    },
  };
}

/** "Genius - The Genius Of Sex · 16:51", "Blog · Who Are You?", "Book · Genius · Chapter Eleven". */
function citeLabel(cite, showTitle) {
  if (cite.kind === 'blog') return `Blog · ${cite.title}`;
  if (cite.kind === 'book') return `Book · ${cite.title}`;
  return showTitle && cite.title ? `${cite.title} · ${cite.time}` : cite.time;
}

/**
 * What a screen reader says for the marker: "Source 1: Genius - The Genius Of
 * Sex, 16:51", "Source 2: Genius, Chapter Eleven, in Rob's book".
 */
function citeSpoken(cite, n) {
  if (cite.kind === 'blog') return `Source ${n}: blog post, ${cite.title}`;
  if (cite.kind === 'book') return `Source ${n}: ${cite.title.replace(/\s*·\s*/g, ', ')}, in Rob's book`;
  const time = cite.end ? `${cite.start} to ${cite.end}` : cite.start;
  return `Source ${n}: ${cite.title ? `${cite.title}, ` : ''}${time}`;
}

/** Where the reader opens for a chapter that was read (ground): data-book-para, a paragraph number. */
const bookParaAttr = (cite) => (Number.isInteger(cite.para) ? ` data-book-para="${cite.para}"` : '');

/**
 * The superscript. A link carries its spoken name; a number that links
 * nowhere (the book) says it in hidden text and keeps the digit itself out of
 * the reading, so "²" is never announced as a bare "2". `more` marks a number
 * that follows another with nothing between them ("¹,²"): it takes the comma,
 * and on a touch screen its tap area stops at its own edge. Where the app has
 * the Genius reader (`bookReader`), a book number is a button that opens the
 * passage (data-book-chapter; components/GeniusReader.js).
 */
function markerHtml(cite, n, showTitle, more, bookReader = false) {
  const tip = escapeHtml(citeLabel(cite, showTitle));
  const spoken = escapeHtml(citeSpoken(cite, n));
  let inner;
  if (cite.url) {
    inner = `<a href="${escapeHtml(cite.url)}" target="_blank" rel="noopener noreferrer" aria-label="${spoken}" title="${tip}">${n}</a>`;
  } else if (bookReader && cite.kind === 'book' && cite.chapter != null) {
    inner = `<button type="button" class="cv-cite-book" data-book-chapter="${cite.chapter}"${bookParaAttr(cite)} data-track="Genius passage" aria-haspopup="dialog" aria-label="${spoken}. Read the passage" title="${tip}">${n}</button>`;
  } else {
    inner = `<span class="cv-cite-sr">${spoken}</span><span class="cv-cite-num" aria-hidden="true" title="${tip}">${n}</span>`;
  }
  return `<sup class="cv-cite${more ? ' cv-cite-more' : ''}">${inner}</sup>`;
}

// A reference whose sentence has already ended ("…troubles. <0> Churches")…
const SENTENCE_ENDED = /[.!?…](?:&quot;|&#39;|[”’)\]*_])*\s*$/;
// …unless that "sentence end" is a list number ("1. <0> Rob explains…")…
const LIST_HEAD = /^[ \t]*(?:\d+[.)]|[-*•]|&gt;)[ \t]*$/;
// …and only when a new sentence, a line end or another reference follows.
const THEN_NEW_SENTENCE = /^[ \t]*(?:$|\n|<\d|[*_]*(?:[A-Z0-9“‘]|&quot;|&#39;))/;

/**
 * Second pass: numbers in reading order (the patterns run by kind, not by
 * position), then each held citation becomes its sentence name plus a
 * superscript — or only the superscript when it trails a finished sentence
 * or stands alone in parentheses.
 */
function placeCitations(text, held, citations, showTitle, bookReader) {
  const re = /<(\d+)>/g;
  let out = '';
  let last = 0;
  let prevBare = false;
  let m;
  while ((m = re.exec(text))) {
    const cite = held[Number(m[1])];
    let before = text.slice(last, m.index);
    const after = text.slice(re.lastIndex);
    const ctx = out + before;
    const line = ctx.slice(ctx.lastIndexOf('\n') + 1);
    // Opening a line with words after it, a reference is that line's subject
    // ("…the goal.\n\n**[Title | 3:00]** Rob says…"): it keeps its name, and
    // its paragraph is not pulled up into the one before.
    const opensLine = /^[ \t]*$/.test(line) && !/^[ \t]*(?:\n|$)/.test(after);
    let bare = false;
    const open = before.match(/[ \t]*\([ \t]*$/);
    const close = after.match(/^[ \t]*\)/);
    if (open && close) {
      before = before.slice(0, open.index);
      re.lastIndex += close[0].length;
      bare = true;
    } else if (!opensLine && THEN_NEW_SENTENCE.test(after)) {
      if (prevBare && /^[\s,;]*$/.test(before)) {
        before = '';
        bare = true;
      } else if (SENTENCE_ENDED.test(ctx) && !LIST_HEAD.test(line)) {
        before = before.replace(/\s+$/, '');
        bare = true;
      }
    }
    const n = citations.number(cite);
    let name = '';
    if (!bare) {
      name = cite.nameIsTime
        ? `<span class="cv-cite-at">${cite.name}</span>`
        : `<cite class="cv-cite-name">${cite.name}</cite>`;
    }
    // Back to back with the number before it, nothing at all between them
    // (decided here: CSS cannot see the words between two numbers).
    const more = bare && /<\/sup>$/.test(out + before);
    out += before + name + markerHtml(cite, n, showTitle, more, bookReader);
    prevBare = bare;
    last = re.lastIndex;
  }
  return out + text.slice(last);
}

/**
 * @param {string} content  HTML-escaped message text
 * @param {Array}  videos   catalog entries { id, title, videoUrl }
 * @param {object} [opts]
 * @param {Array}  [opts.sources]        this answer's retrieved sermons, posts and chapters
 * @param {string} [opts.singleVideoUrl] the one sermon in single mode, for bare [4:12] forms
 * @param {boolean}[opts.showTitle]      name the sermon in labels (multi/archive)
 * @param {object} [opts.citations]      a createCitationList() that collects the numbered sources
 * @param {boolean}[opts.bookReader]     book numbers open the Genius reader (the app; not the portal)
 * @param {object} [opts.ground]         what was in front of the model (groundOf): a book
 *                                       citation is numbered only when its chapter is in
 *                                       ground.book, bracketed or loose (**Genius · Chapter 3**);
 *                                       any other is its name in plain words, with no number,
 *                                       no row and nothing counted. Without it: as always.
 */
export function processSermonTimestampLinks(content, videos = [], opts = {}) {
  if (!content || typeof content !== 'string') return content;
  const { sources = [], singleVideoUrl = null, showTitle = true, bookReader = false } = opts;
  const citations = opts.citations || createCitationList();
  const ground = groundOf(opts.ground);
  const grounded = (chapter) => chapter != null && ground.book.includes(chapter);
  // Where the reader should open for a chapter that was read: the paragraph the
  // server put on its source (a quote it verified, or the part it read), else
  // the first part of it that was read. Only with ground.
  const paraFor = (chapter, source) => {
    if (source && Number.isInteger(source.para)) return source.para;
    const first = ground && ground.at && Array.isArray(ground.at[chapter]) ? ground.at[chapter][0] : null;
    const from = Array.isArray(first) ? first[0] : first && first.from;
    return Number.isInteger(from) ? from : null;
  };
  const bookCite = (citation, name) => {
    const key = chapterKey(citation);
    const chapter = chapterNumberOf(key);
    const source = findBookSource(sources, citation);
    return {
      kind: 'book',
      key: `book ${key || normalizeTitle(citation)}`,
      url: null,
      chapter,
      title: bookTitle(citation, source),
      name: escapeHtml(name),
      ...(ground && paraFor(chapter, source) != null ? { para: paraFor(chapter, source) } : {}),
    };
  };

  const held = [];
  const hold = (cite) => `<${held.push(cite) - 1}>`;
  // A lone ** belongs to bold text around the citation, not to the citation.
  const unwrap = (open, cite, close) => (Boolean(open) === Boolean(close) ? cite : `${open || ''}${cite}${close || ''}`);
  let out = content.replace(LINK_IN_CITATION, (full, before, label, after) =>
    (isCitationShape(before, after) ? `[${before}${label}${after}]` : full));

  // Blog and book citations first: their titles can contain the words and
  // punctuation the sermon patterns look for, so resolving them up front
  // keeps a post called "9:00 or 10:30?" from being read as a timecode.
  out = out.replace(BLOG_PATTERN, (full, open, rawTitle, close) => {
    const match = findBlogSource(sources, rawTitle);
    const title = decodeEntities(match ? match.title : rawTitle);
    return unwrap(open, match
      ? hold({ kind: 'blog', key: `blog ${match.videoUrl}`, url: match.videoUrl, title, name: escapeHtml(title), date: match.publishDate || null })
      : `<cite class="cv-cite-name">${escapeHtml(title)}</cite>`, close);
  });

  out = out.replace(BOOK_PATTERN, (full, open, rawCitation, close) => {
    const citation = decodeEntities(rawCitation).replace(/\s+/g, ' ');
    // In the sentence it reads "Genius, Chapter Eleven".
    const name = citation
      .replace(/^Genius\s*[-–—]?\s*(?=Chapter\b|Introduction\b)/, 'Genius, ')
      .replace(/\s*[·•]\s*/g, ', ');
    // A chapter the book doesn't have is not a source: its name, no number.
    // With ground, neither is one the model never read.
    if ((/\bchapter\b/i.test(citation) && !chapterKey(citation)) || (ground && !grounded(chapterNumberOf(chapterKey(citation))))) {
      return unwrap(open, `<cite class="cv-cite-name">${escapeHtml(name)}</cite>`, close);
    }
    return unwrap(open, hold(bookCite(citation, name)), close);
  });

  if (ground) {
    out = out.replace(BOOK_LOOSE, (full, rawCitation) => {
      const citation = decodeEntities(rawCitation).replace(/\s+/g, ' ').trim();
      const name = citation
        .replace(/^Genius\s*(?:[-–—:,]\s*)?(?=Chapter\b|Introduction\b)/, 'Genius, ')
        .replace(/\s*[·•]\s*/g, ', ');
      const chapter = chapterNumberOf(chapterKey(citation));
      return grounded(chapter) ? hold(bookCite(citation, name)) : `<cite class="cv-cite-name">${escapeHtml(name)}</cite>`;
    });
  }

  const moment = ({ base, title, startTs, endTs, nameIsTime = false, date = null }) => {
    const seconds = parseTimestampToSeconds(startTs);
    const time = endTs ? `${startTs}–${endTs}` : startTs;
    return hold({
      kind: 'video',
      key: `video ${base} ${seconds}`,
      url: buildTimestampUrl(base, seconds),
      title: title || null,
      name: nameIsTime ? time : escapeHtml(title),
      nameIsTime,
      time,
      start: startTs,
      end: endTs || null,
      date,
    });
  };

  const singleBase = baseVideoUrl({ videoUrl: singleVideoUrl });
  for (const pattern of TITLE_TIMESTAMP_PATTERNS) {
    out = out.replace(pattern, (full, videoRef, startTs, endTs) => {
      const target = resolveVideoByTitle(videos, videoRef, sources);
      // On one sermon, any title points at it, unless the title is a stand-in.
      const base = baseVideoUrl(target) || (isPlaceholderTitle(videoRef) ? null : singleBase);
      const title = target?.title || decodeEntities(videoRef).trim();
      if (!isWebUrl(base)) return `<cite class="cv-cite-name">${escapeHtml(title)}</cite> (${endTs ? `${startTs}–${endTs}` : startTs})`;
      return moment({ base, title, startTs, endTs, date: target?.publishDate || (videos.length === 1 ? videos[0].publishDate : null) || null });
    });
  }

  // Bare forms — only meaningful when there is one sermon to point at.
  const videoSources = sources.filter((s) => sourceKind(s) === 'video');
  let one = null;
  if (singleBase) one = { base: singleBase, title: videos.length === 1 ? videos[0].title : null, date: videos.length === 1 ? videos[0].publishDate || null : null };
  else if (videos.length === 1) one = { base: baseVideoUrl(videos[0]), title: videos[0].title, date: videos[0].publishDate || null };
  else if (videoSources.length === 1) one = { base: baseVideoUrl(videoSources[0]), title: videoSources[0].title, date: videoSources[0].publishDate || null };
  if (one && isWebUrl(one.base)) {
    out = out.replace(BARE_BRACKET, (full, startTs, endTs) => moment({ ...one, startTs, endTs, nameIsTime: true }));
    out = out.replace(BARE_WORD, (full, pre, startTs, endTs) => pre + moment({ ...one, startTs, endTs, nameIsTime: true }));
  } else {
    // No target video: still strip the machine-y prefix so text reads cleanly.
    out = out.replace(/\[timestamp:((?:\d{1,2}:)?\d{1,2}:\d{2}(?:-(?:\d{1,2}:)?\d{1,2}:\d{2})?)\]/g, '$1');
  }

  return placeCitations(out, held, citations, showTitle, bookReader);
}

/**
 * The numbered list at the foot of an answer: each distinct source once,
 * linked where it can be (sermon moments, with the same lead-in as the
 * superscripts; blog posts), and the book by chapter.
 *
 * @param {object} citations  the createCitationList() processSermonTimestampLinks filled
 * @param {object} [opts]
 * @param {boolean}[opts.showTitle]  name each sermon (off in single-sermon mode: one sermon, many moments)
 * @returns {string} HTML, or '' when nothing was cited
 */
export function renderCitationFooter(citations, { showTitle = true } = {}) {
  const entries = citations?.entries || [];
  if (!entries.length) return '';
  const items = entries.map((c) => {
    const link = (html) => (c.url
      ? `<a href="${escapeHtml(c.url)}" target="_blank" rel="noopener noreferrer">${html}</a>`
      : `<span>${html}</span>`);
    let body;
    if (c.kind === 'video') {
      const time = `<span class="cv-cites-at">${escapeHtml(c.time)}</span>`;
      body = link(showTitle && c.title ? `${escapeHtml(c.title)} · ${time}` : time);
    } else {
      body = `<span class="cv-cites-kind">${c.kind === 'blog' ? 'Blog' : 'Book'}</span>${link(escapeHtml(c.title))}`;
    }
    return `<li><span class="cv-cites-n">${c.n}</span>${body}</li>`;
  });
  // role="list": Safari stops announcing a list once its markers are styled away.
  return `<div class="cv-cites${showTitle ? '' : ' compact'}"><div class="cv-cites-label">Sources</div><ol class="cv-cites-list" role="list">${items.join('')}</ol></div>`;
}

const SHORT_DATE = { month: 'short', day: 'numeric', year: 'numeric' };

/** "Dec 15, 2024" from the catalog's 'YYYY-MM-DD' (read at noon: no day shift). */
export function formatSourceDate(iso) {
  if (!iso || typeof iso !== 'string') return '';
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', SHORT_DATE);
}

/** "From Rob's sermons", "From Rob's posts", "From Rob's sermons and book"… (BRAND_SPEC §11.1). */
export function sourcesHeading(kinds) {
  const words = [];
  if (kinds.has('video')) words.push('sermons');
  if (kinds.has('blog')) words.push('posts');
  if (kinds.has('book')) words.push('book');
  if (!words.length) return 'From Rob';
  const list = words.length === 1 ? words[0] : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
  return `From Rob’s ${list}`;
}

const ROW_ICONS = {
  // Lucide 1.49.0 (ISC): play, external-link, book-open.
  play: '<path d="M5 5a2 2 0 0 1 3.008-1.728l11.997 6.998a2 2 0 0 1 .003 3.458l-12 7A2 2 0 0 1 5 19z"/>',
  link: '<path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>',
  book: '<path d="M12 5v16"/><path d="M20.001 19A2 2 0 0022 17V5a2 2 0 00-1.999-2L16 3.002A5 5 0 0012 5a5 5 0 00-4-2H4a2 2 0 00-2 2v12a2 2 0 001.999 2H8a5 5 0 014 2 5 5 0 014-2z"/>',
};

const rowIcon = (name) => `<span class="cv-src-go" aria-hidden="true"><svg class="apr-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" focusable="false">${ROW_ICONS[name]}</svg></span>`;

/**
 * The same numbered sources as renderCitationFooter, drawn as BRAND_SPEC
 * §11.1's list: a 24 px number box, the title, "Dec 15, 2024 · plays from
 * 3:48", and a quiet play control. Each row is one link (the whole row is the
 * target) and carries data-cite="n", so a tap on a superscript can scroll to
 * its row instead of leaving the page. The book links nowhere.
 *
 * @param {object} citations  the createCitationList() processSermonTimestampLinks filled
 * @param {object} [opts]
 * @param {boolean}[opts.showTitle]  name each sermon (off on one sermon: its moments, by time)
 * @param {boolean}[opts.bookReader] a chapter's row is a button that opens the Genius reader
 * @returns {string} HTML, or '' when nothing was cited
 */
export function renderSourceRows(citations, { showTitle = true, bookReader = false } = {}) {
  const entries = citations?.entries || [];
  if (!entries.length) return '';
  const kinds = new Set(entries.map((c) => c.kind));
  const rows = entries.map((c) => {
    const date = formatSourceDate(c.date);
    const at = escapeHtml(c.start || c.time || '');
    let title;
    let meta;
    let icon;
    let spoken;
    if (c.kind === 'video') {
      const named = showTitle && c.title;
      title = named ? escapeHtml(c.title) : `Plays from <span class="cv-cites-at">${at}</span>`;
      meta = named
        ? `${date ? `${escapeHtml(date)} · ` : ''}plays from <span class="cv-cites-at">${at}</span>`
        : escapeHtml(date || 'This sermon');
      icon = 'play';
      spoken = `Source ${c.n}: ${c.title ? `${c.title}, ` : ''}${date ? `${date}, ` : ''}plays from ${c.start || c.time}`;
    } else if (c.kind === 'blog') {
      title = escapeHtml(c.title);
      meta = `${date ? `${escapeHtml(date)} · ` : ''}post`;
      icon = 'link';
      spoken = `Source ${c.n}: ${c.title}, ${date ? `${date}, ` : ''}post`;
    } else {
      title = escapeHtml(c.title);
      meta = 'Rob’s book';
      icon = 'book';
      spoken = `Source ${c.n}: ${c.title}, Rob's book`;
    }
    const inner = `<span class="cv-src-n" aria-hidden="true">${c.n}</span><span class="cv-src-text" aria-hidden="true"><span class="cv-src-title">${title}</span><span class="cv-src-meta">${meta}</span></span>${rowIcon(icon)}`;
    if (bookReader && c.kind === 'book' && c.chapter != null) {
      return `<li><button type="button" class="cv-src is-book" data-cite="${c.n}" data-book-chapter="${c.chapter}"${bookParaAttr(c)} data-track="Genius passage" aria-haspopup="dialog" aria-label="${escapeHtml(spoken)}. Read the passage">${inner}</button></li>`;
    }
    return c.url
      ? `<li><a class="cv-src" data-cite="${c.n}" href="${escapeHtml(c.url)}" target="_blank" rel="noopener noreferrer" aria-label="${escapeHtml(spoken)}">${inner}</a></li>`
      : `<li><span class="cv-src is-static" data-cite="${c.n}" tabindex="-1" role="note" aria-label="${escapeHtml(spoken)}">${inner}</span></li>`;
  });
  return `<div class="cv-cites cv-cites--rows"><div class="cv-cites-label">${sourcesHeading(kinds)}</div><ol class="cv-cites-list" role="list">${rows.join('')}</ol></div>`;
}
